import { Controller } from "@hotwired/stimulus"
import { createElement } from "react"
import { createRoot } from "react-dom/client"

import { fullMode } from "../tiptap/modes/full-mode"
import { TiptapEditorShell } from "../tiptap/shell/tiptap-editor-shell"

const EMPTY_PARAGRAPH_PATTERN =
  /^<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>$/i

export default class extends Controller {
  static targets = ["input", "mount"]
  static values = {
    autofocus: { type: Boolean, default: false },
    recordType: { type: String, default: "" },
    recordId: { type: String, default: "" },
  }

  connect() {
    this.handleSetContent = this.handleSetContent.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.updateStickyOffset = this.updateStickyOffset.bind(this)

    this.element.addEventListener("editor:set-content", this.handleSetContent)
    this.element.addEventListener("editor:focus", this.handleFocus)
    window.addEventListener("resize", this.updateStickyOffset)

    this.updateStickyOffset()
    this.mountReactEditor()
  }

  disconnect() {
    this.element.removeEventListener("editor:set-content", this.handleSetContent)
    this.element.removeEventListener("editor:focus", this.handleFocus)
    window.removeEventListener("resize", this.updateStickyOffset)

    if (this.root) {
      this.root.unmount()
      this.root = null
    }

    this.editor = null
  }

  // The editor header pins to the page below whatever fixed/sticky chrome the
  // current layout has (app nav, breadcrumbs toolbar, cpanel title). Layouts
  // differ per page and per breakpoint, so measure instead of hardcoding.
  updateStickyOffset() {
    let offset = 0

    document.querySelectorAll(".header, .toolbar, .cpanel-name").forEach((el) => {
      const style = window.getComputedStyle(el)
      if (style.display === "none" || style.visibility === "hidden") return
      if (style.position !== "fixed" && style.position !== "sticky") return

      const top = parseFloat(style.top) || 0
      offset = Math.max(offset, top + el.offsetHeight)
    })

    if (offset > 0) {
      this.element.style.setProperty("--editor-sticky-top", `${offset}px`)
    }
  }

  mountReactEditor() {
    this.root = createRoot(this.mountTarget)
    this.root.render(
      createElement(TiptapEditorShell, {
        content: this.inputTarget.value || "",
        mode: fullMode,
        recordType: this.recordTypeValue,
        recordId: this.recordIdValue,
        onChange: (html) => this.handleChange(html),
        onReady: (editor) => this.handleReady(editor),
      }),
    )
  }

  handleReady(editor) {
    this.editor = editor

    if (this.autofocusValue) {
      requestAnimationFrame(() => {
        if (this.element.isConnected) {
          this.editor?.commands?.focus?.("end")
        }
      })
    }
  }

  handleChange(html) {
    const normalizedHtml = this.normalizeHTML(html)
    if (this.inputTarget.value === normalizedHtml) return

    this.inputTarget.value = normalizedHtml
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  handleSetContent(event) {
    const { content = "", focus = false } = event.detail || {}
    this.setContent(content, { focus })
  }

  handleFocus() {
    this.editor?.commands?.focus?.("end")
  }

  setContent(content = "", { focus = false } = {}) {
    const normalizedHtml = this.normalizeHTML(content)
    this.inputTarget.value = normalizedHtml
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))

    if (this.editor) {
      this.editor.commands.setContent(normalizedHtml, true)

      if (focus) {
        this.editor.commands.focus("end")
      }
    }
  }

  normalizeHTML(html) {
    const normalizedHtml = typeof html === "string" ? html.trim() : ""
    return EMPTY_PARAGRAPH_PATTERN.test(normalizedHtml) ? "" : normalizedHtml
  }
}
